import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import {
  format,
  parseISO,
  addDays,
  addMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameDay,
  isSameMonth,
  isToday,
  isWeekend,
  nextMonday,
  nextSaturday,
  differenceInCalendarDays,
} from 'date-fns';
import { es } from 'date-fns/locale';

interface DatePickerProps {
  value: string; // 'yyyy-MM-dd' | ''
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  compact?: boolean;
  className?: string;
}

const toISO = (d: Date) => format(d, 'yyyy-MM-dd');
const safeParse = (v: string): Date | null => {
  if (!v) return null;
  try {
    const d = parseISO(v);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

export default function DatePicker({
  value,
  onChange,
  placeholder = 'Seleccionar fecha',
  id,
  disabled,
  compact = false,
  className = '',
}: DatePickerProps) {
  const selected = safeParse(value);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(selected ?? new Date());
  const [focusDate, setFocusDate] = useState<Date>(selected ?? new Date());
  const rootRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setViewMonth(selected ?? new Date());
      setFocusDate(selected ?? new Date());
      requestAnimationFrame(() => popRef.current?.focus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const commit = (d: Date) => {
    onChange(toISO(d));
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    let next: Date | null = null;
    if (e.key === 'ArrowLeft') next = addDays(focusDate, -1);
    else if (e.key === 'ArrowRight') next = addDays(focusDate, 1);
    else if (e.key === 'ArrowUp') next = addDays(focusDate, -7);
    else if (e.key === 'ArrowDown') next = addDays(focusDate, 7);
    else if (e.key === 'Enter') {
      e.preventDefault();
      commit(focusDate);
      return;
    }
    if (next) {
      e.preventDefault();
      setFocusDate(next);
      if (!isSameMonth(next, viewMonth)) setViewMonth(next);
    }
  };

  const today = new Date();
  const presets: { label: string; date: Date }[] = [
    { label: 'Hoy', date: today },
    { label: 'Mañana', date: addDays(today, 1) },
    { label: 'En 3 días', date: addDays(today, 3) },
    { label: 'Próx. lunes', date: nextMonday(today) },
    { label: 'Fin de semana', date: nextSaturday(today) },
  ];

  const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
  const days: Date[] = [];
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d);

  const relHint = () => {
    if (!selected) return null;
    const n = differenceInCalendarDays(selected, today);
    if (n === 0) return 'Es hoy';
    if (n === 1) return 'Mañana';
    if (n === -1) return 'Ayer';
    if (n > 1) return `Faltan ${n} días`;
    return `Hace ${Math.abs(n)} días`;
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={
          compact
            ? 'inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-left text-sm text-gray-200 hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors disabled:opacity-50'
            : 'w-full flex items-center gap-2 px-3 py-2.5 sm:py-2 bg-gray-800 border border-gray-700 rounded-lg text-left text-base sm:text-sm text-gray-200 hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors disabled:opacity-50'
        }
      >
        <Calendar className="w-4 h-4 text-brand-400 flex-shrink-0" />
        <span className={selected ? 'text-gray-100' : 'text-gray-500'}>
          {selected
            ? format(selected, compact ? 'd MMM yyyy' : 'EEE d MMM yyyy', { locale: es })
            : placeholder}
        </span>
        {!compact && (
          <ChevronDown className={`w-4 h-4 text-gray-500 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {open && (
        <div
          ref={popRef}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          className="absolute z-50 mt-2 flex gap-3 p-3 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl focus:outline-none animate-fade-in-up"
          style={{ minWidth: 320 }}
        >
          <div className="flex flex-col gap-0.5 w-28 border-r border-gray-800 pr-2">
            {presets.map((p) => {
              const active = selected && isSameDay(p.date, selected);
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => commit(p.date)}
                  className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-[12.5px] transition-colors ${
                    active ? 'bg-brand-500/15 text-brand-400' : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <span>{p.label}</span>
                  <span className="text-[11px] text-gray-600 tabular-nums">{format(p.date, 'd')}</span>
                </button>
              );
            })}
          </div>

          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={() => setViewMonth(addMonths(viewMonth, -1))}
                className="p-1 rounded-md text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                aria-label="Mes anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-[13px] font-semibold text-gray-100 capitalize">
                {format(viewMonth, 'MMMM yyyy', { locale: es })}
              </span>
              <button
                type="button"
                onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                className="p-1 rounded-md text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                aria-label="Mes siguiente"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] text-gray-600 text-center">
              {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d) => (
                <span key={d}>{d}</span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {days.map((d) => {
                const isSel = selected && isSameDay(d, selected);
                const isFoc = isSameDay(d, focusDate);
                const inMonth = isSameMonth(d, viewMonth);
                const weekend = isWeekend(d);
                let cls = 'text-gray-200';
                if (!inMonth) cls = 'text-gray-600';
                else if (weekend) cls = 'text-gray-500';
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    onClick={() => commit(d)}
                    onMouseEnter={() => setFocusDate(d)}
                    className={`h-8 w-8 flex items-center justify-center text-[12px] rounded-lg tabular-nums transition-colors ${
                      isSel
                        ? 'bg-brand-600 text-white font-semibold'
                        : `${cls} hover:bg-gray-800`
                    } ${isFoc && !isSel ? 'ring-1 ring-brand-500' : ''} ${
                      isToday(d) && !isSel ? 'ring-1 ring-gray-600' : ''
                    }`}
                  >
                    {format(d, 'd')}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-800">
              <span className="text-[11px] text-gray-500">{relHint() ?? 'Sin fecha'}</span>
              {selected && (
                <button
                  type="button"
                  onClick={() => {
                    onChange('');
                    setOpen(false);
                  }}
                  className="text-[11px] text-gray-500 hover:text-danger-400"
                >
                  Limpiar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
